import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

function Section({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export function PrivacyPage() {
  const lastUpdated = "2026-01-27";

  const navigate = useNavigate();
  const onBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  return (
    <>
      <Seo
        title={t`Privacy Policy`}
        description={t`What data Stratistics collects, how it’s used, and your choices.`}
        canonicalPath="/privacy/"
      />
      <div className="flex h-svh w-full overflow-hidden bg-background">
        <div className="container mx-auto max-w-3xl px-4 py-10 flex flex-col">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Button variant="ghost" className="-ml-3" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              <Trans>Back</Trans>
            </Button>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>
                <Trans>Last updated:</Trans> {lastUpdated}
              </span>
            </div>
          </div>

          <Card className="flex-1 overflow-y-auto">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">
                <Trans>Privacy Policy</Trans>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  This policy explains what data Stratistics collects, how it’s
                  used, and the choices you have.
                </Trans>
              </p>
            </CardHeader>

            <CardContent className="space-y-8">
              <Section title={<Trans>Summary</Trans>}>
                <p>
                  <Trans>
                    Stratistics is built to help you analyze your Strava
                    activity data. We aim to collect the minimum data needed to
                    provide the service.
                  </Trans>
                </p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>We do not sell your personal data.</Trans>
                  </li>
                  <li>
                    <Trans>
                      We do not post anything to Strava on your behalf.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      You can disconnect Strava access and request deletion.
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>What we collect</Trans>}>
                <p>
                  <Trans>
                    When you sign in and connect Strava, we may store:
                  </Trans>
                </p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>
                      Account data: email, authentication identifiers, and basic
                      profile settings for your Stratistics account.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Strava connection data: OAuth tokens and related
                      identifiers required to sync your activities.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Activity data: activity metadata (e.g., date, type,
                      distance, time, elevation) and route data to generate
                      heatmaps.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Usage data: basic logs required for security, debugging,
                      and performance (e.g., error reports).
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>How we use your data</Trans>}>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>
                      Provide the app features (charts, calendar, tables, maps).
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Sync your activities from Strava and keep them up to date.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Maintain security, prevent abuse, and troubleshoot issues.
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>Sharing</Trans>}>
                <p>
                  <Trans>
                    We share data only with service providers required to run
                    the app (for example database services). We do not sell your
                    data.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Data retention</Trans>}>
                <p>
                  <Trans>
                    We retain your data for as long as your account is active or
                    as needed to provide the service. You can request deletion
                    of your account and associated data.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Your choices</Trans>}>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>
                      Disconnect Strava: revoke access in Strava settings and/or
                      disconnect in Stratistics.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Delete your account: request deletion to remove stored
                      data.
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>Security</Trans>}>
                <p>
                  <Trans>
                    We use technical measures to protect your data, including
                    access controls and encrypted connections.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Contact</Trans>}>
                <p>stratistics.app@gmail.com</p>
              </Section>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
